import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';

const BatchDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [batch, setBatch] = useState(null);

  useEffect(() => {
    const getBatch = async () => {
      const data = await api.getBatchById({ id });
      setBatch(data);
    };
    getBatch();
  }, [id]);

  const handleToggleOpen = async () => {
    try {
      await api.changeBatchOpen({ batchId: id });

      // Update state di parent
      setBatch((prev) => ({
        ...prev,
        isOpen: !prev.isOpen,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await api.changeBatchStatus({ batchId: id });

      setBatch((prev) => ({
        ...prev,
        statusOffered: !prev.statusOffered,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  if (!batch) {
    return (
      <div className="text-center py-5 text-muted">
        <div className="spinner-border text-primary mb-3" />
        <p>Loading batch details...</p>
      </div>
    );
  }

  return (
    <div className="container py-4">
      {/* Back Button */}
      <div className="mb-3">
        <button
          type="button"
          className="btn btn-white btn-sm rounded-circle shadow-sm border bg-white text-dark d-inline-flex align-items-center justify-content-center"
          style={{ width: '38px', height: '38px' }}
          onClick={() => navigate(-1)}
        >
          <i className="bi bi-arrow-left fs-5"></i>
        </button>
      </div>

      {/* Header */}
      <div className="card border-0 shadow-sm mb-4 rounded-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h5 className="fw-bold mb-1">
                <i className="bi bi-collection me-2 text-primary"></i>
                Batch {batch.batch} — {batch.programName}
              </h5>
              <p className="text-muted mb-2 small">
                {batch.programDescription}
              </p>

              <div className="d-flex flex-wrap small text-muted">
                <div className="me-3">
                  <i className="bi bi-calendar2-event text-primary me-1"></i>
                  {batch.startDate} – {batch.endDate}
                </div>
              </div>
            </div>

            <div className="text-end" style={{ minWidth: '260px' }}>
              {/* STATUS BADGE */}
              <span
                className={`badge rounded-pill text-bg-${
                  batch.statusOffered ? 'success' : 'danger'
                } px-3 py-2 mb-3 d-inline-block`}
              >
                {batch.statusOffered ? 'Running' : 'Closed'}
              </span>
            </div>
          </div>
          {/* CARD FOR SWITCH OPTIONS */}
          <div className="p-3 bg-light rounded-3 shadow-sm small mt-3">
            {/* STATUS OFFERED (RUNNING) */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong className="d-block">Batch sedang berjalan</strong>
                </div>
                <div className="form-check form-switch m-0 p-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={batch.statusOffered}
                    onChange={handleToggleStatus}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
              <p className="text-muted mt-1 mb-0">
                Jika aktif, sistem menganggap batch ini sebagai batch utama
                untuk penilaian, KPI, dan aktivitas peserta.
              </p>
            </div>

            {/* IS OPEN FOR REGISTRATION */}
            <div>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong className="d-block">
                    Batch dibuka untuk pendaftaran
                  </strong>
                </div>
                <div className="form-check form-switch m-0 p-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={batch.isOpen}
                    onChange={handleToggleOpen}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
              <p className="text-muted mt-1 mb-0">
                Jika aktif, batch muncul di halaman pendaftaran dan calon
                peserta dapat mendaftar.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body">
          <div className="d-flex align-items-center mb-3 justify-content-between">
            <h6 className="fw-semibold mb-0">
              <i className="bi bi-diagram-3-fill text-primary me-2"></i>Tahapan
              Program
            </h6>
            <button
              className="btn btn-sm btn-primary"
              onClick={() =>
                navigate(`/batch/${id}/add-section`, {
                  state: { programId: batch.programId },
                })
              }
            >
              <i className="bi bi-plus-lg me-1"></i> Tambah Tahapan
            </button>
          </div>

          {batch.sections && batch.sections.length > 0 ? (
            <div className="timeline position-relative ps-4">
              {batch.sections.map((sec, idx) => (
                <div
                  key={sec.id}
                  className="timeline-item position-relative ps-4 mb-4"
                  style={{
                    borderLeft: '2px solid var(--bs-primary)',
                    marginLeft: '10px',
                  }}
                >
                  {/* Marker (bulatan kecil di garis) */}
                  <span
                    className={`position-absolute top-0 start-0 translate-middle rounded-circle ${
                      sec.statusActive ? 'bg-success' : 'bg-secondary'
                    }`}
                    style={{
                      width: '12px',
                      height: '12px',
                      left: '-6px',
                      top: '10px',
                      zIndex: 2,
                    }}
                  ></span>

                  <div className="ms-2">
                    <div className="d-flex justify-content-between align-items-start flex-wrap">
                      <div>
                        <h6 className="fw-semibold mb-1">{sec.section}</h6>
                        <p className="text-muted small mb-2">
                          {sec.sectionDescription}
                        </p>
                        <div className="small text-muted">
                          <i className="bi bi-calendar2-week me-1 text-primary"></i>
                          {sec.startDate} – {sec.endDate}
                        </div>
                      </div>
                      <div>
                        <span
                          className={`badge rounded-pill text-bg-${
                            sec.statusActive ? 'success' : 'secondary'
                          } px-3 py-2`}
                        >
                          {sec.statusActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted small text-center py-4">
              <i className="bi bi-inbox me-1"></i> Tidak ada section
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchDetail;
